/**
 * useTimelineFilterState Hook
 *
 * Manages debounced search state and synchronization with parent filters.
 * Handles the complex logic of keeping local search state in sync with
 * parent-controlled filters while debouncing updates.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import type { TimelineFilters } from "../types/timeline-filters";

interface UseTimelineFilterStateProps {
  filters: TimelineFilters;
  onFiltersChange: (filters: TimelineFilters) => void;
}

interface UseTimelineFilterStateReturn {
  localSearch: string;
  debouncedSearch: string;
  setLocalSearch: (value: string) => void;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearAllFilters: () => void;
}

/**
 * Hook for managing timeline filter state with debounced search
 *
 * @param filters - Current filter values
 * @param onFiltersChange - Callback when filters change
 * @returns Filter state and handlers
 */
export function useTimelineFilterState({
  filters,
  onFiltersChange,
}: UseTimelineFilterStateProps): UseTimelineFilterStateReturn {
  // Local debounced search state
  const {
    searchValue: localSearch,
    debouncedSearchValue: debouncedSearch,
    setSearchValue: setLocalSearch,
  } = useDebouncedSearch({
    delay: 300,
    initialValue: filters.search || "",
  });

  // Track previous debounced search to prevent infinite loops
  const previousDebouncedSearchRef = useRef<string>(debouncedSearch);
  const filtersRef = useRef(filters);
  const previousFiltersSearchRef = useRef<string>(filters.search || "");

  // Keep filters ref in sync
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Sync local search when filters.search changes from outside (e.g., clear all)
  useEffect(() => {
    const currentFiltersSearch = filters.search || "";
    const previousFiltersSearch = previousFiltersSearchRef.current;

    // If filters.search changed from outside and doesn't match local search, sync it
    if (
      currentFiltersSearch !== previousFiltersSearch &&
      currentFiltersSearch !== localSearch
    ) {
      setLocalSearch(currentFiltersSearch);
    }

    previousFiltersSearchRef.current = currentFiltersSearch;
  }, [filters.search, localSearch, setLocalSearch]);

  // Update parent filters when debounced search changes
  useEffect(() => {
    const currentDebouncedSearch = debouncedSearch || "";
    const previousDebouncedSearch = previousDebouncedSearchRef.current;
    const currentFilters = filtersRef.current;
    const currentFiltersSearch = currentFilters.search || "";

    // Only update if:
    // 1. The debounced search value actually changed (not just a re-render)
    // 2. The current filter search doesn't already match the debounced search
    if (
      currentDebouncedSearch !== previousDebouncedSearch &&
      currentDebouncedSearch !== currentFiltersSearch
    ) {
      onFiltersChange({
        ...currentFilters,
        search: debouncedSearch || undefined,
      });
    }

    // Update ref for next comparison
    previousDebouncedSearchRef.current = currentDebouncedSearch;
  }, [debouncedSearch, onFiltersChange]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalSearch(e.target.value);
    },
    [setLocalSearch]
  );

  const clearAllFilters = useCallback(() => {
    onFiltersChange({});
  }, [onFiltersChange]);

  return {
    localSearch,
    debouncedSearch,
    setLocalSearch,
    handleSearchChange,
    clearAllFilters,
  };
}
